import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Col, Container, Row, Spinner } from 'react-bootstrap'
import { NotificationRequestModal } from './NotificationRequestModal'
import { ZoomIntegrationRow } from './Zoom/ZoomIntegrationRow'
import { Divider } from '../../../components/Divider/Divider'
import { GoogleCalendarIntegrationRow } from './GoogleCalendar/GoogleCalendarIntegrationRow'
import { TeamsIntegrationRow } from './Teams/TeamsIntegrationRow'
import { WealthboxIntegrationRow } from './Wealthbox/WealthboxIntegrationRow'
import { SalesforceIntegrationRow } from './Salesforce/SalesforceIntegrationRow'
import { MeetIntegrationRow } from './Meet/MeetIntegrationRow'
import { OutlookCalendarIntegrationRow } from './OutlookCalendar/OutlookCalendarIntegrationRow'
import { RedtailIntegrationRow } from './Redtail/RedtailIntegrationRow'
import { PhoneIntegrationRow } from './Phone/PhoneIntegrationRow'
import { PractifiIntegrationRow } from './Practifi/PractifiIntegrationRow'
import { XLR8IntegrationRow } from './XLR8/XLR8IntegrationRow'
import { OutlookMailIntegrationRow } from './OutlookMail/OutlookMailIntegrationRow'
import { GmailIntegrationRow } from './Gmail/GmailIntegrationRow'
import { ZohoIntegrationRow } from './Zoho/ZohoIntegrationRow'
import { HubSpotIntegrationRow } from './HubSpot/HubSpotIntegrationRow'
import { DynamicsIntegrationRow } from './Dynamics/DynamicsIntegrationRow'
import { ExchangeCalendarIntegrationRow } from './ExchangeCalendar/ExchangeCalendarIntegrationRow'
import { SmartOfficeIntegrationRow } from './SmartOffice/SmartOfficeIntegrationRow'
import { EMoneyIntegrationRow } from './EMoney/EMoneyIntegrationRow'
import { PreciseFPIntegrationRow } from './PreciseFP/PreciseFPIntegrationRow'

import { useGetAccountQuery } from '../../../store/accountUserSettings/accountUserSettings'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { IntegrationServiceId } from '../../../common/integrations'
import { ElementsIntegrationRow } from './Elements/ElementsIntegrationRow'
import { BlackDiamondIntegrationRow } from './BlackDiamond/BlackDiamondIntegrationRow'
import { OrionIntegrationRow } from './Orion/OrionIntegrationRow'
import { WebexIntegrationRow } from './Webex/WebexIntegrationRow'
import { EngageIntegrationRow } from './Engage/EngageSettingsRow'
import { AdvisorEngineIntegrationRow } from './AdvisorEngine/AdvisorEngineIntegrationRow'
import { HolistiplanIntegrationRow } from './Holsitiplan/HolistiplanIntegrationRow'
import { IntegrationSetupConfig } from '../../../models/Account'
import { CLICIntegrationRow } from './CLIC/CLICIntegrationRow'
import { RightCapitalIntegrationRow } from './RightCapital/RightCapitalIntegrationRow'
import { useIntegrations } from '../../../apps/professional-app/fa/common/hooks/useIntegrations'

export const IntegrationsTab: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isLoading } = useGetAccountQuery({ accountId })
  const { isIntegrationAllowed } = useIntegrations()

  const [showNotificationRequestModal, setShowNotificationRequestModal] = useState(false)

  const handleSaveNotification = () => {
    setShowNotificationRequestModal(false)
  }

  const handleHideNotificationModal = () => {
    setShowNotificationRequestModal(false)
  }

  const [searchParams] = useSearchParams()
  const [integrationToOpen, setIntegrationToOpen] = useState<string | null>(null)

  const integrationSetupConfigs = account?.data?.enterpriseConfig.integrationsConfig.integrationSetupConfigs ?? []
  const setupConfigMap = Object.fromEntries(integrationSetupConfigs.map((c: IntegrationSetupConfig) => [c.integration, c]))

  const handleIntegrationClick = (integration: IntegrationServiceId, defaultAuth: () => void) => {
    const config = setupConfigMap[integration]

    if (config?.url) {
      if (config.openInNewTab) {
        window.open(config?.url, '_blank', 'noopener noreferrer')
      } else {
        window.location.href = config?.url
      }
    } else {
      defaultAuth()
    }
  }

  useEffect(() => {
    const crm = searchParams.get('crm')
    const connector = searchParams.get('connector')

    if (crm) {
      setIntegrationToOpen(crm)
    } else if (connector) {
      setIntegrationToOpen(connector)
    } else {
      setIntegrationToOpen(null)
    }
  }, [searchParams])

  const integrations = {
    meeting: [
      ...(isIntegrationAllowed(IntegrationServiceId.PHONE) ? [<PhoneIntegrationRow />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.ZOOM) ? [<ZoomIntegrationRow />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.TEAMS) ? [<TeamsIntegrationRow />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.MEET) ? [<MeetIntegrationRow />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.WEBEX) ? [<WebexIntegrationRow />] : []),
    ],
    calendar: [
      ...(isIntegrationAllowed(IntegrationServiceId.GOOGLE_CALENDAR) ? [<GoogleCalendarIntegrationRow />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.OUTLOOK_CALENDAR)
        ? [<OutlookCalendarIntegrationRow onClick={handleIntegrationClick} triggerAuth={integrationToOpen === 'outlookCalendar'} />]
        : []),
      ...(isIntegrationAllowed(IntegrationServiceId.EXCHANGE_CALENDAR) ? [<ExchangeCalendarIntegrationRow />] : []),
    ],
    email: [
      ...(isIntegrationAllowed(IntegrationServiceId.GMAIL) ? [<GmailIntegrationRow />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.OUTLOOK_MAIL) ? [<OutlookMailIntegrationRow />] : []),
    ],
    crm: [
      ...(isIntegrationAllowed(IntegrationServiceId.WEALTHBOX) ? [<WealthboxIntegrationRow />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.SALESFORCE) ? [<SalesforceIntegrationRow />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.PRACTIFI) ? [<PractifiIntegrationRow />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.XLR8) ? [<XLR8IntegrationRow />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.ELEMENTS) ? [<ElementsIntegrationRow />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.BLACK_DIAMOND) ? [<BlackDiamondIntegrationRow />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.REDTAIL) ? [<RedtailIntegrationRow />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.ZOHO) ? [<ZohoIntegrationRow />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.HUBSPOT) ? [<HubSpotIntegrationRow />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.DYNAMICS) ? [<DynamicsIntegrationRow />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.ENGAGE) ? [<EngageIntegrationRow />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.SMARTOFFICE) ? [<SmartOfficeIntegrationRow openModal={integrationToOpen === 'smartoffice'} />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.ADVISOR_ENGINE) ? [<AdvisorEngineIntegrationRow />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.CLIC) ? [<CLICIntegrationRow />] : []),
    ],
    planning: [
      ...(isIntegrationAllowed(IntegrationServiceId.E_MONEY) ? [<EMoneyIntegrationRow />] : []),
      ...(isIntegrationAllowed(IntegrationServiceId.RIGHT_CAPITAL) ? [<RightCapitalIntegrationRow />] : []),
    ],
    portfolio: [...(isIntegrationAllowed(IntegrationServiceId.ORION) ? [<OrionIntegrationRow />] : [])],
    tax: [...(isIntegrationAllowed(IntegrationServiceId.HOLISTIPLAN) ? [<HolistiplanIntegrationRow />] : [])],
    onboarding: [...(isIntegrationAllowed(IntegrationServiceId.PRECISE_FP) ? [<PreciseFPIntegrationRow />] : [])],
  }

  if (isLoading) {
    return (
      <Container className='p-0'>
        <NotificationRequestModal show={showNotificationRequestModal} onHide={handleHideNotificationModal} onSave={handleSaveNotification} />
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      </Container>
    )
  }

  return (
    <Container className='p-0'>
      <NotificationRequestModal show={showNotificationRequestModal} onHide={handleHideNotificationModal} onSave={handleSaveNotification} />
      {integrations.meeting.length > 0 ? (
        <>
          <Row className='pb-3'>
            <Col>
              <h4>Integration</h4>
              <div>Schedule a meeting in your calendar</div>
            </Col>
          </Row>
          <Row>
            <Col>
              <Integrations elements={integrations.meeting} />
            </Col>
          </Row>
        </>
      ) : null}
      {integrations.calendar.length > 0 ? (
        <>
          <Row className='mt-5'>
            <Col>
              <h4>Schedule a meeting in your calendar</h4>
              <div>Schedule a meeting in your calendar</div>
            </Col>
          </Row>
          <Row>
            <Col>
              <Integrations elements={integrations.calendar} />
            </Col>
          </Row>
        </>
      ) : null}
      {integrations.email.length > 0 ? (
        <>
          <Row className='mt-5'>
            <Col>
              <h4>Connect your email</h4>
            </Col>
          </Row>
          <Row>
            <Col>
              <Integrations elements={integrations.email} />
            </Col>
          </Row>
        </>
      ) : null}
      {integrations.planning.length > 0 ? (
        <>
          <Row className='mt-5'>
            <Col>
              <h4>Connect to Financial Planning</h4>
            </Col>
          </Row>
          <Integrations elements={integrations.planning} />
        </>
      ) : null}
      {integrations.portfolio.length > 0 ? (
        <>
          <Row className='mt-5'>
            <Col>
              <h4>Connect to Portfolio Management & Reporting</h4>
            </Col>
          </Row>
          <Integrations elements={integrations.portfolio} />
        </>
      ) : null}
      {integrations.tax.length > 0 ? (
        <>
          <Row className='mt-5'>
            <Col>
              <h4>Connect to Tax Planning</h4>
            </Col>
          </Row>
          <Integrations elements={integrations.tax} />
        </>
      ) : null}
      {integrations.crm.length > 0 ? (
        <>
          <Row className='mt-5'>
            <Col>
              <h4>Connect to CRM</h4>
            </Col>
          </Row>
          <Integrations elements={integrations.crm} />
        </>
      ) : null}
      {integrations.onboarding.length > 0 ? (
        <>
          <Row className='mt-5'>
            <Col>
              <h4>Connect to Forms & Client Onboarding</h4>
            </Col>
          </Row>
          <Integrations elements={integrations.onboarding} />
        </>
      ) : null}
    </Container>
  )
}

const Integrations: React.FC<{ elements: React.ReactNode[] }> = ({ elements }) => {
  return (
    <>
      {elements.map((integration, i, arr) => (
        <>
          <Row className='pb-3 pb-md-0'>
            <Col>{integration}</Col>
          </Row>
          {i + 1 < arr.length && <Separator />}
        </>
      ))}
    </>
  )
}

const Separator: React.FC = () => <Divider className='d-none d-md-block' />
